import { ref, get, set, push } from "firebase/database";
import { db } from "../configs/Firebase";

/**
 * Migrates brands and countries from customer-specific paths to top-level paths
 * @param {string} orgId - Organization ID
 * @returns {Promise<{brandsMigrated: boolean, countriesMigrated: boolean, error: string|null}>}
 */
export async function migrateBrandsCountries(orgId) {
  if (!orgId) {
    return {
      brandsMigrated: false,
      countriesMigrated: false,
      error: "Organization ID is required"
    };
  }

  const results = {
    brandsMigrated: false,
    countriesMigrated: false,
    error: null
  };

  try {
    // Check if top-level brands already exist
    const topLevelBrandsRef = ref(db, `userData/${orgId}/brands`);
    const topLevelBrandsSnapshot = await get(topLevelBrandsRef);
    
    // Check if top-level countries already exist
    const topLevelCountriesRef = ref(db, `userData/${orgId}/countries`);
    const topLevelCountriesSnapshot = await get(topLevelCountriesRef);

    // Migrate brands if top-level doesn't exist
    if (!topLevelBrandsSnapshot.exists()) {
      const customerBrandsRef = ref(db, `userData/${orgId}/customers/brands`);
      const customerBrandsSnapshot = await get(customerBrandsRef);
      
      if (customerBrandsSnapshot.exists()) {
        const customerBrands = customerBrandsSnapshot.val();
        const brandsToMigrate = {};
        
        // Copy all brands, preserving IDs
        for (const brandId in customerBrands) {
          brandsToMigrate[brandId] = {
            ...customerBrands[brandId]
          };
        }
        
        if (Object.keys(brandsToMigrate).length > 0) {
          await set(topLevelBrandsRef, brandsToMigrate);
          results.brandsMigrated = true;
        }
      } else {
        // Create empty brands structure
        await set(topLevelBrandsRef, {});
        results.brandsMigrated = true;
      }
    }

    // Migrate countries if top-level doesn't exist
    if (!topLevelCountriesSnapshot.exists()) {
      const customerCountriesRef = ref(db, `userData/${orgId}/customers/countries`);
      const customerCountriesSnapshot = await get(customerCountriesRef);
      
      if (customerCountriesSnapshot.exists()) {
        const customerCountries = customerCountriesSnapshot.val();
        const countriesToMigrate = {};
        
        // Copy all countries, preserving IDs
        for (const countryId in customerCountries) {
          countriesToMigrate[countryId] = {
            ...customerCountries[countryId]
          };
        }
        
        if (Object.keys(countriesToMigrate).length > 0) {
          await set(topLevelCountriesRef, countriesToMigrate);
          results.countriesMigrated = true;
        }
      } else {
        // Create empty countries structure
        await set(topLevelCountriesRef, {});
        results.countriesMigrated = true;
      }
    }

    return results;
  } catch (error) {
    console.error("Error migrating brands and countries:", error);
    return {
      brandsMigrated: false,
      countriesMigrated: false,
      error: error.message
    };
  }
}

/**
 * Checks if migration is needed
 * @param {string} orgId - Organization ID
 * @returns {Promise<{needsMigration: boolean, brandsExist: boolean, countriesExist: boolean}>}
 */
export async function checkMigrationNeeded(orgId) {
  if (!orgId) {
    return {
      needsMigration: false,
      brandsExist: false,
      countriesExist: false
    };
  }

  try {
    const topLevelBrandsRef = ref(db, `userData/${orgId}/brands`);
    const topLevelCountriesRef = ref(db, `userData/${orgId}/countries`);
    
    const [brandsSnapshot, countriesSnapshot] = await Promise.all([
      get(topLevelBrandsRef),
      get(topLevelCountriesRef)
    ]);

    const brandsExist = brandsSnapshot.exists();
    const countriesExist = countriesSnapshot.exists();

    return {
      needsMigration: !brandsExist || !countriesExist,
      brandsExist,
      countriesExist
    };
  } catch (error) {
    console.error("Error checking migration status:", error);
    return {
      needsMigration: false,
      brandsExist: false,
      countriesExist: false
    };
  }
}

